'use client';

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Splitter, IconButton, Tooltip, Text } from '@terra/ui-design-system';
import {
  AlertCircleIcon,
  FileCodeIcon,
  FolderLibraryIcon,
  GitBranchIcon,
  GitPullRequestIcon,
  RefreshIcon,
} from '@terra/ui-icons';
import { terraProxyApiClient } from '../clients/testerClient';
import { useAppTargetId } from '../store/projectStore';
import { useBottomPanel } from '../store/layoutStore';
import { FileTree } from './FileTree';
import { FileContentViewer } from './FileContentViewer';
import { EntryPointsList } from './code/EntryPointsList';
import type { EndpointFile } from '@terra/shared-contracts-git';
import { EmptyState } from './EmptyState';

interface CodeModuleProps {
  endpointId?: string;
}
const getEndpointFiles = async (applicationId: string, endpointId?: string) => {
  if (endpointId) {
    const result = await terraProxyApiClient.getEndpointFiles(
      applicationId,
      endpointId,
    );

    return result?.files || [];
  }

  const result = await terraProxyApiClient.getAllEndpointFiles(applicationId);

  return result?.files || [];
};

export function CodeModule({ endpointId }: CodeModuleProps) {
  const appTargetId = useAppTargetId();
  const {
    setTab: setBottomTab,
    selectedFileId,
    setSelectedFileId,
  } = useBottomPanel();
  const [selectedFile, setSelectedFile] = useState<EndpointFile | null>(null);
  const [selectedView, setSelectedView] = useState<'content' | 'changes'>(
    'content',
  );
  const {
    data: files,
    isLoading: isLoadingFiles,
    error: filesError,
    refetch,
  } = useQuery({
    queryKey: ['endpoint-files', appTargetId, endpointId],
    queryFn: async () => {
      if (!appTargetId) return [];

      return getEndpointFiles(appTargetId, endpointId);
    },
    enabled: !!appTargetId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  // Handle selectedFileId changes from graph node clicks
  useEffect(() => {
    if (selectedFileId && files) {
      // Find the file that matches the selectedFileId (file path)
      const matchingFile =
        files.find((file: EndpointFile) => file.filePath === selectedFileId) ??
        files.find(
          (file: EndpointFile) =>
            selectedFileId.endsWith(`/${file.filePath}`) ||
            file.filePath.endsWith(`/${selectedFileId}`),
        );

      if (matchingFile) {
        setSelectedFile(matchingFile);

        if (matchingFile.filePath !== selectedFileId) {
          setSelectedFileId?.(matchingFile.filePath);
        }
      }
    }
  }, [selectedFileId, files, setSelectedFileId]);
  const handleViewCodeGraph = () => {
    setBottomTab('code-graph');
  };

  const handleFileSelect = (file: EndpointFile) => {
    setSelectedFile(file);
    setSelectedFileId?.(file.filePath);
  };

  if (isLoadingFiles) {
    return (
      <EmptyState
        icon={FileCodeIcon}
        title="Loading related files..."
        isLoading={true}
      />
    );
  }

  if (filesError || !files || files.length === 0) {
    return (
      <EmptyState
        icon={AlertCircleIcon}
        title="No Code Files Found"
        description="Code graph generation may be in progress or not yet triggered for this endpoint."
      />
    );
  }

  return (
    <Splitter.Root
      orientation="horizontal"
      h="100%"
      defaultSize={[30, 70]}
      panels={[{ id: 'file-tree' }, { id: 'file-content' }]}
      borderWidth="0"
      gap={0}
    >
      <Splitter.Panel id="file-tree" borderWidth="0">
        <Box
          h="full"
          overflow="hidden"
          display="flex"
          flexDirection="column"
          w="full"
        >
          <FileTree
            files={files}
            selectedFileId={selectedFileId}
            onFileSelect={handleFileSelect}
          />
        </Box>
      </Splitter.Panel>

      {/* Resize Handle */}
      <Splitter.ResizeTrigger
        id="file-tree:file-content"
        width="0px"
        minWidth="0px"
        bg="transparent"
        position="relative"
        _after={{
          content: '""',
          position: 'absolute',
          inset: '0 4px 0 -4px',
          width: '10px',
          bg: 'transparent',
          cursor: 'col-resize',
        }}
      />

      <Splitter.Panel id="file-content" borderWidth="0">
        <VStack
          gap={0}
          alignItems="stretch"
          h="full"
          w="full"
          overflow="hidden"
        >
          <HStack
            justifyContent="space-between"
            alignItems="center"
            flexShrink={0}
            px={3}
            py={1}
            borderBottomWidth="1px"
            borderColor="border.subtle"
          >
            <Text fontSize="xs" fontWeight="semibold" color="fg.default">
              {selectedView === 'changes'
                ? 'Changes'
                : selectedFile?.nodeName || 'Select a file'}
            </Text>

            <HStack gap={0}>
              <Tooltip title="File Content">
                <IconButton
                  size="xs"
                  variant="ghost"
                  onClick={() => setSelectedView('content')}
                  aria-label="File Content"
                  bg={selectedView === 'content' ? 'bg.muted' : undefined}
                >
                  <FolderLibraryIcon size={14} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Attack Surface Changes">
                <IconButton
                  size="xs"
                  variant="ghost"
                  onClick={() => setSelectedView('changes')}
                  aria-label="Attack Surface Changes"
                  bg={selectedView === 'changes' ? 'bg.muted' : undefined}
                >
                  <GitPullRequestIcon size={14} />
                </IconButton>
              </Tooltip>

              <Tooltip title="View Code Graph">
                <IconButton
                  size="xs"
                  variant="ghost"
                  onClick={handleViewCodeGraph}
                  aria-label="View code graph"
                >
                  <GitBranchIcon size={14} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Refresh Files">
                <IconButton
                  size="xs"
                  variant="ghost"
                  onClick={() => {
                    refetch();
                  }}
                  aria-label="Refresh files"
                >
                  <RefreshIcon size={14} />
                </IconButton>
              </Tooltip>
            </HStack>
          </HStack>

          <Box flex="1" minH={0} overflow="hidden" w="full">
            {selectedView === 'content' ? (
              <FileContentViewer
                file={selectedFile || undefined}
                isLoading={false}
              />
            ) : (
              <EntryPointsList
                endpointId={endpointId}
                onViewGraph={handleViewCodeGraph}
              />
            )}
          </Box>
        </VStack>
      </Splitter.Panel>
    </Splitter.Root>
  );
}
